'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Check } from 'lucide-react'
import { usePostHog } from '@posthog/next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { visScanSchema } from '@/lib/tools/validators'

const TRUST_ITEMS = ['Free, no signup', 'Results in 30 seconds'] as const

export function HeroDomainForm() {
  const router = useRouter()
  const posthog = usePostHog()
  const [domain, setDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    const parsed = visScanSchema.safeParse({ domain })
    if (!parsed.success) {
      setError('Please enter a valid domain (e.g. yourcompany.com)')
      return
    }

    // No domain property: the browser sends this event with the visitor's IP, which would put the
    // scanned domain beside it in PostHog (R6.6 AC-12: the domain reaches PostHog from our servers).
    posthog?.capture('hero_domain_submitted')
    setIsPending(true)
    router.push(`/audit?domain=${encodeURIComponent(parsed.data.domain)}`)
  }

  return (
    <div className="mt-8 max-w-[560px]">
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Input
            type="text"
            inputSize="lg"
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value)
              if (error) setError(null)
            }}
            placeholder="yourcompany.com"
            aria-label="Your company domain"
            aria-describedby={error ? 'hero-domain-error' : undefined}
            aria-invalid={error ? true : undefined}
            disabled={isPending}
            className="flex-1"
          />
          <Button type="submit" size="lg" pending={isPending} className="w-full sm:w-auto">
            Run free scan
          </Button>
        </div>

        {error && (
          <p id="hero-domain-error" role="alert" className="mt-1 text-caption text-destructive">
            {error}
          </p>
        )}
      </form>

      {/* Trust strip */}
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
        {TRUST_ITEMS.map((item) => (
          <div key={item} className="flex items-center gap-1.5">
            <Check size={14} className="text-emerald shrink-0" aria-hidden="true" />
            <span className="text-caption text-muted-foreground">{item}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
